import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Mail, Phone, MapPin } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/landing/Footer';
import WhatsAppWidget from '../components/WhatsAppWidget';

const LAST_UPDATED = 'August 5, 2026';

const SECTIONS = [
  {
    id: 'information-we-collect',
    title: '1. Information We Collect',
    body: (
      <>
        <p>We collect information you provide directly to us, such as when you fill out an enquiry, demo-booking, or brochure-download form on this website. This may include:</p>
        <ul className="list-disc pl-5 space-y-1.5 mt-3">
          <li>Your full name</li>
          <li>Email address</li>
          <li>Phone number</li>
          <li>The program or course you're interested in, and any message you choose to add</li>
        </ul>
        <p className="mt-3">We may also automatically collect limited technical information — such as which page or program you were viewing when you submitted a form — to help our team follow up on the right enquiry.</p>
      </>
    ),
  },
  {
    id: 'how-we-use',
    title: '2. How We Use Your Information',
    body: (
      <>
        <p>We use the information you provide to:</p>
        <ul className="list-disc pl-5 space-y-1.5 mt-3">
          <li>Respond to your enquiry and share details about our training programs</li>
          <li>Get in touch by phone, email, or WhatsApp regarding a course, demo class, or callback you requested</li>
          <li>Send you brochures or program-related materials you've asked for</li>
          <li>Improve our website and the programs we offer</li>
        </ul>
        <p className="mt-3">We do not use your information to make automated decisions that have a legal or similarly significant effect on you.</p>
      </>
    ),
  },
  {
    id: 'cookies',
    title: '3. Cookies & Similar Technologies',
    body: (
      <p>
        We use minimal, functional browser storage (such as session storage) to remember things like whether you've
        already seen a popup during your current visit, so we don't show it to you twice. We do not use this to
        track you across other websites.
      </p>
    ),
  },
  {
    id: 'sharing',
    title: '4. How We Share Your Information',
    body: (
      <>
        <p>We do not sell your personal information. We may share it only with:</p>
        <ul className="list-disc pl-5 space-y-1.5 mt-3">
          <li>Our internal counsellors and trainers, so they can follow up on your enquiry</li>
          <li>Service providers who help us operate the website and manage enquiries on our behalf (for example, our CRM and communication tools), under an obligation to keep it confidential</li>
          <li>Authorities, where required to comply with a legal obligation</li>
        </ul>
      </>
    ),
  },
  {
    id: 'security',
    title: '5. Data Security',
    body: (
      <p>
        We take reasonable technical and organizational measures to protect the information you share with us from
        unauthorized access, loss, or misuse. That said, no method of transmission over the internet is 100% secure,
        and we cannot guarantee absolute security.
      </p>
    ),
  },
  {
    id: 'retention',
    title: '6. Data Retention',
    body: (
      <p>
        We retain enquiry and lead information for as long as reasonably needed to respond to you, support your
        enrollment, and meet our internal record-keeping and legal requirements — after which it is deleted or
        anonymized.
      </p>
    ),
  },
  {
    id: 'your-rights',
    title: '7. Your Rights & Choices',
    body: (
      <>
        <p>You can ask us to:</p>
        <ul className="list-disc pl-5 space-y-1.5 mt-3">
          <li>Share what personal information we hold about you</li>
          <li>Correct inaccurate information</li>
          <li>Delete your information from our systems, or stop contacting you</li>
        </ul>
        <p className="mt-3">
          To make any of these requests, write to us at{' '}
          <a href="mailto:info@a7globetech.com" className="font-semibold text-[#0056D2] hover:underline">info@a7globetech.com</a>{' '}
          and we'll get back to you.
        </p>
      </>
    ),
  },
  {
    id: 'third-party',
    title: '8. Third-Party Links & Services',
    body: (
      <p>
        Our website links out to a small number of third-party services — for example, WhatsApp (for chat) and our
        Interview Readiness Platform. If you choose to interact with these, your information is also subject to
        their own privacy policies, which we encourage you to review.
      </p>
    ),
  },
  {
    id: 'children',
    title: "9. Children's Privacy",
    body: (
      <p>
        Our programs are intended for adult learners and working professionals. We do not knowingly collect
        personal information from children under 18.
      </p>
    ),
  },
  {
    id: 'changes',
    title: '10. Changes to This Policy',
    body: (
      <p>
        We may update this Privacy Policy from time to time to reflect changes in our practices. We'll update the
        "Last updated" date above whenever we do, so please check back periodically.
      </p>
    ),
  },
];

export default function PrivacyPolicyPage() {
  return (
    <div data-testid="privacy-policy-page">
      <Navbar />

      {/* Hero */}
      <section className="pt-[104px] bg-gradient-to-br from-[#0056D2] via-[#003E99] to-[#002B6B]">
        <div className="max-w-5xl mx-auto px-6 lg:px-12 py-16 lg:py-20 text-center">
          <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm text-white/90 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Shield className="w-4 h-4" />
            Privacy Policy
          </div>
          <h1 className="font-heading font-black text-3xl md:text-4xl lg:text-5xl text-white leading-tight mb-4">
            Your privacy matters to us
          </h1>
          <p className="font-body text-base md:text-lg text-white/80 max-w-2xl mx-auto leading-relaxed">
            This page explains what information A7 Globetech collects when you use this website, why we collect it,
            and the choices you have.
          </p>
          <p className="font-body text-sm text-white/60 mt-5">Last updated: {LAST_UPDATED}</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 lg:py-20 bg-[#F7F9FC]">
        <div className="max-w-5xl mx-auto px-6 lg:px-12 grid lg:grid-cols-[220px_1fr] gap-10">
          {/* Table of contents */}
          <nav className="hidden lg:block sticky top-24 self-start">
            <p className="font-body text-xs font-semibold text-[#9AA5B5] uppercase tracking-wider mb-3">On this page</p>
            <ul className="space-y-2">
              {SECTIONS.map(s => (
                <li key={s.id}>
                  <a
                    href={`#${s.id}`}
                    className="font-body text-sm text-[#5A6A85] hover:text-[#0056D2] transition-colors block"
                  >
                    {s.title}
                  </a>
                </li>
              ))}
              <li>
                <a href="#contact" className="font-body text-sm text-[#5A6A85] hover:text-[#0056D2] transition-colors block">
                  11. Contact Us
                </a>
              </li>
            </ul>
          </nav>

          {/* Body */}
          <div className="bg-white rounded-2xl border border-[#E1E7EF] p-8 lg:p-10">
            <p className="font-body text-sm text-[#5A6A85] leading-relaxed mb-10">
              A7 Globetech LLP ("A7 Globetech", "we", "us", or "our") operates this website to provide information
              about, and enrollment into, our software technologies training programs. This Privacy Policy describes
              how we collect, use, and protect information about visitors and prospective learners who interact with
              this website. By using this website, you agree to the practices described here.
            </p>

            <div className="space-y-10">
              {SECTIONS.map(s => (
                <div key={s.id} id={s.id} className="scroll-mt-24">
                  <h2 className="font-heading font-bold text-xl text-[#0F1F3D] mb-3">{s.title}</h2>
                  <div className="font-body text-sm text-[#5A6A85] leading-relaxed">{s.body}</div>
                </div>
              ))}

              <div id="contact" className="scroll-mt-24">
                <h2 className="font-heading font-bold text-xl text-[#0F1F3D] mb-3">11. Contact Us</h2>
                <p className="font-body text-sm text-[#5A6A85] leading-relaxed mb-4">
                  If you have any questions about this Privacy Policy or how we handle your information, reach out
                  to us:
                </p>
                <div className="space-y-2.5">
                  <a href="mailto:info@a7globetech.com" className="flex items-center gap-2 font-body text-sm text-[#5A6A85] hover:text-[#0056D2] transition-colors w-fit">
                    <Mail className="w-4 h-4 text-[#0056D2] flex-shrink-0" />
                    info@a7globetech.com
                  </a>
                  <a href="tel:+919032809977" className="flex items-center gap-2 font-body text-sm text-[#5A6A85] hover:text-[#0056D2] transition-colors w-fit">
                    <Phone className="w-4 h-4 text-[#0056D2] flex-shrink-0" />
                    +91 90328 09977
                  </a>
                  <p className="flex items-start gap-2 font-body text-sm text-[#5A6A85]">
                    <MapPin className="w-4 h-4 text-[#0056D2] flex-shrink-0 mt-0.5" />
                    Flat No 501, Navya Gardenia, Phase-III, Kamalapuri Colony, Banjara Hills,
                    Sagar Society, Srinagar Colony, Hyderabad, India 500073
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-10 pt-6 border-t border-[#E1E7EF]">
              <Link to="/" className="font-body text-sm font-semibold text-[#0056D2] hover:underline">
                ← Back to home
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppWidget />
    </div>
  );
}
